import { supabase } from "@/integrations/supabase/client";
import { Customer } from "@/lib/data";

export async function getCustomerById(customerId: string): Promise<Customer | null> {
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .eq('id', customerId)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching customer:', error);
    throw new Error(`Failed to fetch customer: ${error.message}`);
  }
  
  return data as Customer;
}

// New function for paginated customers with total count
export async function getPaginatedCustomers(
  page: number = 1, 
  pageSize: number = 10,
  searchTerm: string = '',
  sortField: string = 'customer_number', 
  sortDirection: 'asc' | 'desc' = 'desc'
): Promise<{ data: Customer[]; count: number }> {
  // Calculate the range based on page and pageSize
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  
  console.log(`Fetching paginated customers: page=${page}, pageSize=${pageSize}, sort=${sortField} ${sortDirection}`);
  
  let query = supabase
    .from('customers')
    .select('*', { count: 'exact' });

  // Apply search if provided (at least 3 characters)
  if (searchTerm && searchTerm.length >= 3) {
    console.log(`Searching for: "${searchTerm}"`);
    query = query.or(
      `customer_name.ilike.%${searchTerm}%,` +
      `customer_number.ilike.%${searchTerm}%,` +
      `email.ilike.%${searchTerm}%,` +
      `first_name.ilike.%${searchTerm}%,` +
      `last_name.ilike.%${searchTerm}%`
    );
  }

  // Apply sorting
  query = query.order(sortField, { ascending: sortDirection === 'asc' });
  
  // Apply pagination
  query = query.range(from, to);
  
  const { data, error, count } = await query;
  
  if (error) {
    console.error('Error fetching customers:', error);
    throw new Error(`Failed to fetch customers: ${error.message}`);
  }
  
  return { 
    data: data as Customer[], 
    count: count || 0 
  };
}

// Keep the original function for backward compatibility
export async function getCustomers(): Promise<Customer[]> {
  console.log(`Fetching all customers`);
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .order('customer_name');
  
  if (error) {
    console.error('Error fetching customers:', error);
    throw new Error(`Failed to fetch customers: ${error.message}`);
  }
  
  return data as Customer[];
}

export async function searchCustomers(searchTerm: string): Promise<Customer[]> {
  if (!searchTerm || searchTerm.length < 3) {
    return [];
  }

  console.log(`Searching customers with term: "${searchTerm}"`);
  
  // Fixed search to avoid using ilike on UUID fields
  const { data, error } = await supabase
    .from('customers')
    .select('*')
    .or(
      `customer_name.ilike.%${searchTerm}%,` +
      `customer_number.ilike.%${searchTerm}%,` +
      `email.ilike.%${searchTerm}%,` +
      `first_name.ilike.%${searchTerm}%,` +
      `last_name.ilike.%${searchTerm}%`
    )
    .order('customer_name')
    .limit(50);
  
  if (error) {
    console.error('Error searching customers:', error);
    throw new Error(`Failed to search customers: ${error.message}`);
  }
  
  console.log(`Search results for "${searchTerm}":`, data?.length || 0);
  return data as Customer[];
}

// Helper function to generate a new customer number
async function generateCustomerNumber(): Promise<string> {
  // Get the highest customer number currently in use
  const { data, error } = await supabase
    .from('customers')
    .select('customer_number')
    .not('customer_number', 'is', null)
    .order('customer_number', { ascending: false })
    .limit(1);
    
  if (error) {
    console.error('Error finding highest customer number:', error);
    throw new Error(`Failed to generate customer number: ${error.message}`);
  }
  
  let nextNumber = 1000; // Start from 1000 if no customers exist yet
  
  if (data && data.length > 0 && data[0].customer_number) {
    // Extract the numeric part and increment
    const currentNumber = parseInt(data[0].customer_number);
    if (!isNaN(currentNumber)) {
      nextNumber = currentNumber + 1;
    }
  }
  
  console.log(`Generated new customer number: ${nextNumber}`);
  return nextNumber.toString();
}

export async function createCustomer(customerData: {
  customer_name: string;
  customer_number?: string;
  customer_type?: 'private' | 'company' | 'reseller';
  phone?: string;
  email?: string;
  organisation_number?: string;
  contact_firstname?: string;
  contact_lastname?: string;
  postal_street?: string;
  postal_zip_code?: string;
  postal_city?: string;
  first_name?: string;
  last_name?: string;
}): Promise<Customer> {
  const { data: sessionData } = await supabase.auth.getSession();
  
  if (!sessionData.session) {
    console.error('No authenticated session found when creating customer');
    throw new Error('You must be logged in to create a customer');
  }
  
  // Generate a customer number if one wasn't provided
  let customerNumberToUse = customerData.customer_number;
  
  if (!customerNumberToUse) {
    customerNumberToUse = await generateCustomerNumber();
    console.log('Auto-generated customer number:', customerNumberToUse);
  } else {
    // Check if the provided customer number already exists
    const { data: existingCustomer, error: checkError } = await supabase
      .from('customers')
      .select('customer_number')
      .eq('customer_number', customerNumberToUse)
      .maybeSingle();
    
    if (checkError) {
      console.error('Error checking for existing customer:', checkError);
      throw new Error(`Failed to create customer: ${checkError.message}`);
    }
    
    if (existingCustomer) {
      throw new Error(`Customer with number ${customerNumberToUse} already exists`);
    }
  }
  
  console.log("Creating customer in database:", customerData.customer_name);
  
  const { data: localCustomer, error: insertError } = await supabase
    .from('customers')
    .insert({
      customer_name: customerData.customer_name,
      customer_number: customerNumberToUse,
      customer_type: customerData.customer_type || 'private',
      phone: customerData.phone || undefined,
      email: customerData.email || undefined,
      organisation_number: customerData.organisation_number || undefined,
      contact_firstname: customerData.contact_firstname || undefined,
      contact_lastname: customerData.contact_lastname || undefined,
      postal_street: customerData.postal_street || undefined,
      postal_zip_code: customerData.postal_zip_code || undefined,
      postal_city: customerData.postal_city || undefined,
      first_name: customerData.first_name || undefined,
      last_name: customerData.last_name || undefined
    })
    .select('*')
    .single();
  
  if (insertError) {
    console.error('Error creating customer in database:', insertError);
    throw new Error(`Failed to create customer: ${insertError.message}`);
  }
  
  return localCustomer as Customer;
}

export async function updateCustomer(
  customerId: string,
  customerData: Partial<Customer>
): Promise<Customer> {
  const { data: sessionData } = await supabase.auth.getSession();
  
  if (!sessionData.session) {
    console.error('No authenticated session found when updating customer');
    throw new Error('You must be logged in to update a customer');
  }
  
  if (customerData.customer_number) {
    const { data: existingCustomer, error: checkError } = await supabase
      .from('customers')
      .select('id, customer_number')
      .eq('customer_number', customerData.customer_number)
      .not('id', 'eq', customerId)
      .maybeSingle();
    
    if (checkError) {
      console.error('Error checking for existing customer:', checkError);
      throw new Error(`Failed to update customer: ${checkError.message}`);
    }
    
    if (existingCustomer) {
      throw new Error(`Another customer with number ${customerData.customer_number} already exists`);
    }
  }
  
  const { data, error } = await supabase
    .from('customers')
    .update(customerData)
    .eq('id', customerId)
    .select('*')
    .single();
  
  if (error) {
    console.error('Error updating customer:', error);
    throw new Error(`Failed to update customer: ${error.message}`);
  }
  
  return data as Customer;
}

export async function deleteCustomer(customerId: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  
  if (!sessionData.session) {
    console.error('No authenticated session found when deleting customer');
    throw new Error('You must be logged in to delete a customer');
  }
  
  const { data: subscriptions, error: subscriptionError } = await supabase
    .from('subscriptions')
    .select('subscription_id')
    .eq('customer_id', customerId);
  
  if (subscriptionError) {
    console.error('Error checking subscriptions for customer:', subscriptionError);
    throw new Error(`Failed to check customer dependencies: ${subscriptionError.message}`);
  }
  
  if (subscriptions && subscriptions.length > 0) {
    throw new Error('Cannot delete customer with active subscriptions');
  }
  
  const { error } = await supabase
    .from('customers')
    .delete()
    .eq('id', customerId);
  
  if (error) {
    console.error('Error deleting customer:', error);
    throw new Error(`Failed to delete customer: ${error.message}`);
  }
}
